/**
 * Where a grid divider is actually a seam.
 *
 * A divider between two columns used to run the grid's full height. With the
 * screener docked on top and spanning every column, that drew a draggable line
 * straight through the screener, where there is nothing to separate. A divider
 * belongs only where the two tracks beside it hold different cells, so this
 * returns the runs of cross-axis tracks where no cell straddles the boundary.
 *
 * Pure and tested, for `layout-templates.ts`'s reason.
 */

/** One cell, as tracks on the axis being divided and on the one across it. */
export interface GridCell {
  start: number;
  span: number;
  crossStart: number;
  crossSpan: number;
}

/** A run of cross-axis tracks, `from` inclusive and `to` exclusive. */
export interface SeamRun {
  from: number;
  to: number;
}

/**
 * The runs along the boundary between track `boundary` and `boundary + 1`.
 *
 * A cross track with no cell in it still counts as a seam: an empty slot is
 * not straddled, and dropping the divider there would split one line in two.
 */
export function seamRuns(
  cells: readonly GridCell[],
  boundary: number,
  crossCount: number,
): SeamRun[] {
  const straddled = new Array<boolean>(crossCount).fill(false);
  for (const cell of cells) {
    const last = cell.start + cell.span - 1;
    if (cell.start > boundary || last < boundary + 1) continue;
    for (let t = cell.crossStart; t < cell.crossStart + cell.crossSpan && t < crossCount; t++) {
      straddled[t] = true;
    }
  }
  const runs: SeamRun[] = [];
  let from = -1;
  for (let t = 0; t <= crossCount; t++) {
    const open = t < crossCount && !straddled[t];
    if (open && from < 0) from = t;
    if (!open && from >= 0) {
      runs.push({ from, to: t });
      from = -1;
    }
  }
  return runs;
}
